import { defineStore } from 'pinia'

export interface SsoUser {
  id: number
  email: string
  name: string
  first_name: string
  last_name: string
  avatar_url: string | null
  role: string
  global_role: string
  groups: { id: number, name: string }[]
  /** Connecté au SSO, mais sans accès à cette application : voir `acces-refuse.vue`. */
  sansAcces?: boolean
  /**
   * Profil de secours : le SSO n'a pas répondu et aucun profil n'était connu.
   * Nom vide, rôle minimal — les droits réels n'ont pas pu être lus.
   */
  degrade?: boolean
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<SsoUser | null>(null)
  const loading = ref(true)
  const loggingOut = ref(false)
  const authenticated = computed(() => !!user.value)
  /** Droits non vérifiés : voir `degrade` sur `SsoUser`. */
  const profilNonVerifie = computed(() => user.value?.degrade === true)

  /**
   * Droits d'administration, au sens de `requireAdmin` (`server/utils/auth.ts`).
   *
   * La règle est recopiée du serveur, qui reste l'autorité : ce booléen ne sert
   * qu'à masquer ce qui serait de toute façon refusé — un écran caché n'est pas
   * un écran protégé.
   */
  const isAdmin = computed(() => {
    if (!user.value) return false
    return ['admin', 'superadmin'].includes(user.value.role)
      || user.value.global_role === 'superadmin'
  })

  /**
   * Droits de superadministration, au sens de `requireSuperAdmin`.
   *
   * Plus étroit qu'`isAdmin`, qui accepte aussi les administrateurs ordinaires.
   * Réservé aux actions qui dépassent la gestion du contenu et engagent des
   * données que plus rien ne pourra rendre.
   */
  /**
   * Droit de modifier les contenus, au sens de `peutEcrire`
   * (`server/utils/auth.ts`) : éditeur, administrateur, ou administrateur
   * global. Un lecteur (`viewer`) consulte.
   *
   * Sert à prévenir et à éteindre les boutons ; le serveur refuse de toute
   * façon.
   */
  const peutEcrire = computed(() => {
    if (!user.value) return false
    return ['editor', 'admin', 'superadmin'].includes(user.value.role)
      || ['admin', 'superadmin'].includes(user.value.global_role)
  })

  const isSuperAdmin = computed(() => {
    if (!user.value) return false
    return user.value.role === 'superadmin' || user.value.global_role === 'superadmin'
  })

  /**
   * Droits de superadministration **de l'annuaire**, au sens de
   * `requireGlobalSuperAdmin`.
   *
   * Encore plus étroit qu'`isSuperAdmin` : le rôle porté par cette seule
   * application ne suffit pas. Réservé aux sauvegardes, dont une archive
   * emporte l'intégralité du contenu du site et dont la restauration écrase
   * tout ce qui a été écrit depuis.
   */
  const isGlobalSuperAdmin = computed(() => {
    if (!user.value) return false
    return user.value.global_role === 'superadmin'
  })

  let lastFetchAt = 0

  async function fetchSession() {
    // Éviter les appels multiples en moins de 2 secondes
    const now = Date.now()
    if (now - lastFetchAt < 2000 && !loading.value) return
    lastFetchAt = now

    loading.value = true
    try {
      const headers = import.meta.server ? useRequestHeaders(['cookie']) : undefined

      /*
       * Signature déclarée plutôt qu'inférée : les en-têtes optionnels côté
       * serveur font diverger la résolution de route de Nitro (TS2589).
       */
      const lireSession = $fetch as unknown as (
        url: string,
        options: { headers: Record<string, string> | undefined },
      ) => Promise<{ success: boolean, data: { user: SsoUser } }>

      const res = await lireSession('/api/auth/session', { headers })
      user.value = res.data.user
    }
    catch {
      user.value = null
    }
    finally {
      loading.value = false
    }
  }

  async function logout() {
    loggingOut.value = true
    const config = useRuntimeConfig()
    if (import.meta.client) {
      try {
        const deconnecter = $fetch as unknown as (
          url: string,
          options: { method: 'POST' },
        ) => Promise<unknown>

        await deconnecter('/api/auth/logout', { method: 'POST' })
      }
      catch {
        // Ignorer
      }
      window.location.href = `${config.public.ssoUrl}/api/auth/logout`
    }
  }

  return {
    user,
    loading,
    loggingOut,
    authenticated,
    profilNonVerifie,
    isAdmin,
    isSuperAdmin,
    peutEcrire,
    isGlobalSuperAdmin,
    fetchSession,
    logout,
  }
})
